/**
 * ResponsePolicyForm — "Create Policy" (F039 Autonomous Governance).
 *
 * Creates (or versions — the server treats an existing `externalId` as a new version of the
 * same logical policy) a response policy that drives the Rule-4 approval queue / autonomous
 * response engine. `POST /api/portal/autonomous-governance/policies`.
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from `ResponsePolicyCreate.schema.ts`
 * (never redefined), Fluent `Field` for verbatim `V-` messages, server errors mapped via
 * `setError` (Charter M3). The outer `Dialog` chrome is owned by `AutonomousGovernancePage.tsx`
 * (`src/pages/**`) — this component renders the form CONTENT only and keeps its existing
 * `onSuccess`/`onCancel`/`onDirtyChange` prop contract so the hosting page needs no change.
 */
import { useState } from 'react';
import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dropdown, Field, Input, Option, Textarea, makeStyles, tokens } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { useMutation } from '@tanstack/react-query';
import { autonomousGovernanceApi } from '../../../services/api';
import type { CreateResponsePolicyInput, ResponsePolicy } from '../../../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import {
  RESPONSE_TRIGGER_KIND_OPTIONS,
  RESPONSE_SEVERITY_OPTIONS,
  RESPONSE_ACTION_TYPE_OPTIONS,
  RESPONSE_IMPACT_CLASS_OPTIONS,
} from './responsePolicyFormFields';
import {
  ResponsePolicyCreateSchema,
  type ResponsePolicyCreateFormValues,
  type ResponsePolicyCreateFormOutput,
} from './ResponsePolicyCreate.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping (B-03/A-01).
const KNOWN_FIELDS = [
  'externalId',
  'name',
  'description',
  'appliesToTrigger',
  'minSeverity',
  'conditionExpression',
  'action',
  'impactOverride',
  'priority',
  'debounceWindow',
] as const;

const DEFAULT_VALUES: ResponsePolicyCreateFormValues = {
  externalId: '',
  name: '',
  description: '',
  appliesToTrigger: '',
  minSeverity: '',
  conditionExpression: '',
  action: '',
  impactOverride: '',
  priority: '',
  debounceWindow: '',
};

export interface ResponsePolicyFormProps {
  onSuccess?: (policy: ResponsePolicy) => void;
  onCancel?: () => void;
  /** Reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const ResponsePolicyForm: FC<ResponsePolicyFormProps> = ({ onSuccess, onCancel, onDirtyChange }) => {
  const styles = useStyles();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isDirty },
  } = useForm<ResponsePolicyCreateFormValues, unknown, ResponsePolicyCreateFormOutput>({
    resolver: zodResolver(ResponsePolicyCreateSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const [globalError, setGlobalError] = useState<string | undefined>();

  const createMutation = useMutation({
    mutationFn: (input: CreateResponsePolicyInput) => autonomousGovernanceApi.createPolicy(input),
    onSuccess: (policy) => {
      reset(DEFAULT_VALUES);
      onSuccess?.(policy);
    },
    onError: (error) => {
      const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
      setGlobalError(globalMessage);
    },
  });

  const onValid = (values: ResponsePolicyCreateFormOutput) => {
    // Guard double-submit.
    if (createMutation.isPending) return;
    setGlobalError(undefined);

    const input: CreateResponsePolicyInput = {
      externalId: values.externalId,
      name: values.name,
      description: values.description,
      appliesToTrigger: values.appliesToTrigger,
      minSeverity: values.minSeverity,
      conditionExpression: values.conditionExpression,
      action: values.action,
      impactOverride: values.impactOverride,
      priority: values.priority,
      debounceWindow: values.debounceWindow,
    };

    createMutation.mutate(input);
  };

  return (
    // `noValidate` — Fluent's `Field required` clones `required`/`aria-required` onto the child
    // control, which would otherwise let the browser's native constraint validation intercept an
    // empty-required submit before react-hook-form's own validation runs.
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner
        title="Unable to Create Policy"
        message={globalError}
        testId="response-policy-form-server-error"
      />

      <Field
        label="External Id"
        required
        validationState={errors.externalId ? 'error' : undefined}
        validationMessage={errors.externalId?.message}
      >
        <Controller
          name="externalId"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="response-policy-form-externalId"
              value={field.value ?? ''}
              placeholder="Stable policy identifier"
              disabled={createMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Name"
        required
        validationState={errors.name ? 'error' : undefined}
        validationMessage={errors.name?.message}
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="response-policy-form-name"
              value={field.value ?? ''}
              placeholder="Human-readable policy name"
              disabled={createMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Description"
        validationState={errors.description ? 'error' : undefined}
        validationMessage={errors.description?.message}
      >
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              data-testid="response-policy-form-description"
              value={field.value ?? ''}
              placeholder="Optional description of intent"
              disabled={createMutation.isPending}
              resize="vertical"
            />
          )}
        />
      </Field>

      <Field
        label="Applies To Trigger"
        required
        validationState={errors.appliesToTrigger ? 'error' : undefined}
        validationMessage={errors.appliesToTrigger?.message}
      >
        <Controller
          name="appliesToTrigger"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={field.value ? [field.value] : []}
              disabled={createMutation.isPending}
              placeholder="Select a trigger kind"
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? '')}
              onBlur={field.onBlur}
              data-testid="response-policy-form-appliesToTrigger"
            >
              {RESPONSE_TRIGGER_KIND_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field
        label="Min Severity"
        required
        validationState={errors.minSeverity ? 'error' : undefined}
        validationMessage={errors.minSeverity?.message}
      >
        <Controller
          name="minSeverity"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={field.value ? [field.value] : []}
              disabled={createMutation.isPending}
              placeholder="Select a minimum severity"
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? '')}
              onBlur={field.onBlur}
              data-testid="response-policy-form-minSeverity"
            >
              {RESPONSE_SEVERITY_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field
        label="Condition Expression"
        required
        hint="key=='value' — AND-joined for multiple conditions"
        validationState={errors.conditionExpression ? 'error' : undefined}
        validationMessage={errors.conditionExpression?.message}
      >
        <Controller
          name="conditionExpression"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="response-policy-form-conditionExpression"
              value={field.value ?? ''}
              placeholder="e.g. severity=='High'"
              disabled={createMutation.isPending}
              maxLength={2000}
            />
          )}
        />
      </Field>

      <Field
        label="Action"
        required
        validationState={errors.action ? 'error' : undefined}
        validationMessage={errors.action?.message}
      >
        <Controller
          name="action"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={field.value ? [field.value] : []}
              disabled={createMutation.isPending}
              placeholder="Select a response action"
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? '')}
              onBlur={field.onBlur}
              data-testid="response-policy-form-action"
            >
              {RESPONSE_ACTION_TYPE_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field label="Impact Override" hint="Optional — overrides the computed impact class">
        <Controller
          name="impactOverride"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={field.value ? [field.value] : []}
              disabled={createMutation.isPending}
              placeholder="No override"
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? '')}
              onBlur={field.onBlur}
              data-testid="response-policy-form-impactOverride"
            >
              {RESPONSE_IMPACT_CLASS_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field
        label="Priority"
        required
        validationState={errors.priority ? 'error' : undefined}
        validationMessage={errors.priority?.message}
      >
        <Controller
          name="priority"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="number"
              data-testid="response-policy-form-priority"
              value={field.value ?? ''}
              placeholder="Lower runs first"
              disabled={createMutation.isPending}
            />
          )}
        />
      </Field>

      <Field
        label="Debounce Window"
        hint="Optional .NET TimeSpan, e.g. 00:05:00 — omit for the server default"
        validationState={errors.debounceWindow ? 'error' : undefined}
        validationMessage={errors.debounceWindow?.message}
      >
        <Controller
          name="debounceWindow"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="response-policy-form-debounceWindow"
              value={field.value ?? ''}
              placeholder="00:05:00"
              disabled={createMutation.isPending}
            />
          )}
        />
      </Field>

      <FormActions
        mode="create"
        testIdPrefix="response-policy-form"
        isSubmitting={createMutation.isPending}
        onCancel={onCancel}
        submitLabel="Create Policy"
        submittingLabel="Creating…"
        submitIcon={<AddRegular />}
      />
    </form>
  );
};
